import type { Trial } from "@/api_types/school";
import FetchService from "@/services/fetch.service";
import { defineStore } from "pinia";

export interface TrialMembership {
  trial_id: string;
  school_name: string;
  district_name: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  firstname: string;
  lastname: string;
  email: string;
  phone_number: string;
  username: string;
  status: 'requested' | 'pending' | 'fullfilled' | 'expired' | 'cancelled' | 'rejected' | 'converted';
  timezone: string;
  num_students?: number;
  demo_date?: string | null;
  created_tstamp: string;
  modified_tstamp: string;
  domain?: string;
  // New fields from the API join
  school_id?: number;
  school_district_id?: number;
  subdomain?: string;
  is_fulfilled?: number; // 1 if truly fulfilled (status = 'fullfilled' AND has matching school), 0 otherwise
}

export interface TrialStats {
  total: number;
  requested: number;
  pending: number;
  fulfilled: number;
  expired: number;
  cancelled: number;
  rejected: number;
  recent: number;
}

export const useTrials = defineStore("support/trials", {
  state: () => ({
    trials: [] as TrialMembership[],
    loading: false,
    stats: {
      total: 0,
      requested: 0,
      pending: 0,
      fulfilled: 0,
      expired: 0,
      cancelled: 0,
      rejected: 0,
      recent: 0
    } as TrialStats,
    statsLoading: false
  }),

  getters: {
    /**
     * Check if a trial can be approved (not truly fulfilled yet)
     */
    canApproveTrial: (state) => {
      return (trialId: string) => {
        const trial = state.trials.find(t => t.trial_id === trialId);
        if (!trial) return false;

        // Can approve if status is requested
        return trial.status === 'requested';
      };
    },

    /**
     * Check if trial is truly fulfilled (has matching school record)
     */
    isTrulyFulfilled: (state) => {
      return (trialId: string) => {
        const trial = state.trials.find(t => t.trial_id === trialId);
        return trial?.is_fulfilled === 1;
      };
    }
  },

  actions: {
    async loadTrials(filters: { status?: string } = {}) {
      this.loading = true;

      try {
        const queryParams = new URLSearchParams();

        // Only send status filter to server, search will be handled client-side
        if (filters.status && filters.status !== 'all') queryParams.append('status', filters.status);

        const url = `/trials${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

        const response = await FetchService.fetchData(url, "get") as {
          data: TrialMembership[];
        } | TrialMembership[];

        // Handle both paginated response format and direct array format
        if (response && 'data' in response) {
          this.trials = response.data || [];
        } else if (Array.isArray(response)) {
          this.trials = response;
        } else {
          this.trials = [];
        }

        // Sort by created date (newest first)
        this.trials.sort((a, b) =>
          new Date(b.created_tstamp).getTime() - new Date(a.created_tstamp).getTime()
        );

      } catch (error) {
        console.error('Error loading trials:', error);
        this.trials = [];
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      this.statsLoading = true;

      try {
        const response = await FetchService.fetchData("/trials-stats", "get") as TrialStats;
        this.stats = response || this.stats;
      } catch (error) {
        console.error('Error loading trial stats:', error);
      } finally {
        this.statsLoading = false;
      }
    },

    async createTrial(trial: Trial): Promise<{ status: boolean; message?: string }> {
      try {
        const res = await FetchService.fetchData(`/trials`, "post", trial, true);
        if (res.res.ok) {
          // Reload trials and stats after successful creation
          await this.loadTrials();
          await this.loadStats();
          return { status: true, message: "Trial created successfully" };
        } else {
          return { status: false, message: res.data?.message || res.data || "Failed to create trial" };
        }
      } catch (error) {
        console.error('Error creating trial:', error);
        return { status: false, message: "Failed to create trial" };
      }
    },

    async updateTrial(
      trialId: string,
      trialData: Partial<TrialMembership>
    ): Promise<{ status: boolean; message?: string; data?: TrialMembership }> {
      try {
        console.log("Update trial");
        console.log(trialData)
        const res = await FetchService.fetchData(`/trials/${trialId}`, "put", trialData, true);
        if (res.res.ok) {
          console.log('Update response:', res.res.status, res.data); // Debug log

          // Update local data with the response that includes school information
          const index = this.trials.findIndex((t) => t.trial_id === trialId);
          if (index !== -1 && res.data) {
            console.log('Before update:', this.trials[index]); // Debug log
            // Replace the entire trial object to ensure reactivity
            console.log("Replacing trial with response data");
            console.log(res.data.data);
            this.trials.splice(index, 1, res.data.data);
            console.log('After update:', this.trials[index]); // Debug log
          } else {
            // If for some reason we can't find the trial or no data returned, reload all trials
            console.warn('Trial not found in local state or no data returned, reloading trials');
            await this.loadTrials();
          }
          await this.loadStats();
          return { status: true, message: "Trial updated successfully", data: res.data.data };
        } else {
          console.error('Update failed:', res.res.status, res.data); // Debug log
          return { status: false, message: res.data?.message || res.data || "Failed to update trial" };
        }
      } catch (error) {
        console.error('Error updating trial:', error);
        return { status: false, message: "Failed to update trial" };
      }
    },

    async updateTrialStatus(
      trialId: string,
      status: TrialMembership["status"]
    ): Promise<{ status: boolean; message?: string; data?: TrialMembership }> {
      return this.updateTrial(trialId, { status });
    },

    async deleteTrial(trialId: string): Promise<{ status: boolean; message?: string }> {
      try {
        const res = await FetchService.fetchData(`/trials/${trialId}`, "delete", undefined, true);
        if (res.res.ok) {
          // Remove from local data
          const index = this.trials.findIndex((t) => t.trial_id === trialId);
          if (index !== -1) {
            this.trials.splice(index, 1);
          }
          await this.loadStats();
          return { status: true, message: "Trial deleted successfully" };
        } else {
          return { status: false, message: res.data?.message || res.data || "Failed to delete trial" };
        }
      } catch (error) {
        console.error('Error deleting trial:', error);
        return { status: false, message: "Failed to delete trial" };
      }
    },

    async getTrial(trialId: string): Promise<TrialMembership | null> {
      try {
        const res = await FetchService.fetchData(`/trials/${trialId}`, "get") as TrialMembership;
        return res || null;
      } catch (error) {
        console.error('Error getting trial:', error);
        return null;
      }
    },

    async scheduleDemo(
      trialId: string,
      demoDate: string
    ): Promise<{ status: boolean; message?: string; data?: TrialMembership; zoom_join_url?: string; google_event_id?: string; warnings?: string[] }> {
      try {
        const res = await FetchService.fetchData(
          `/trials/${trialId}/schedule-demo`,
          "post",
          { demo_date: demoDate },
          true
        );
        if (res.res.ok) {
          // Update local state
          const index = this.trials.findIndex((t) => t.trial_id === trialId);
          if (index !== -1 && res.data?.data) {
            this.trials.splice(index, 1, res.data.data);
          }
          return {
            status: true,
            message: res.data?.message || 'Demo scheduled successfully',
            data: res.data?.data,
            zoom_join_url: res.data?.zoom_join_url,
            google_event_id: res.data?.google_event_id,
            warnings: res.data?.warnings || [],
          };
        } else {
          return { status: false, message: res.data?.message || res.data || 'Failed to schedule demo' };
        }
      } catch (error) {
        console.error('Error scheduling demo:', error);
        return { status: false, message: 'Failed to schedule demo' };
      }
    }
  }
});

export default useTrials;
